/**
 * Keeps per-campaign navigation, status transitions, and downloads together
 * so interactive row actions remain isolated from table-level selection.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTableRow, DataTableCell } from '@amzn/meridian/data-table';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { CampaignStatuses, WorkflowStatuses } from 'src/store/constants';
import { type CampaignDesiredStateDto, type UpdateCampaignDto, type WorkflowDto } from 'src/store/generated/leela-api-generated';
import { useCampaignsControllerUpdateMutation, useCampaignsControllerGetPublishedQuery, useWorkflowsControllerFindManyQuery } from 'src/store/leela-api';
import { getErrorsByField } from 'src/utils';
import { downloadFromS3 } from 'src/utils/S3Download';
import { type RTKWrappedError } from 'src/components/common/forms/types';
import { useCanPerform } from 'src/hooks/useCanPerform';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import MeridianLoader from '@amzn/meridian/loader';
import Tooltip from '@amzn/meridian/tooltip';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import menuMeatballTokens from '@amzn/meridian-tokens/base/icon/menu-meatball';
import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import pauseTokens from '@amzn/meridian-tokens/base/icon/pause';
import playTokens from '@amzn/meridian-tokens/base/icon/play';
import downloadSmallTokens from '@amzn/meridian-tokens/base/icon/download-small';
import archiveTokens from '@amzn/meridian-tokens/base/icon/archive';
import checkCircleFilledTokens from '@amzn/meridian-tokens/base/icon/check-circle-filled';
import alertFilledTokens from '@amzn/meridian-tokens/base/icon/alert-filled';
import clockTokens from '@amzn/meridian-tokens/base/icon/clock';
import { DateTime } from 'luxon';
import Text from '@amzn/meridian/text';
import Tag from '@amzn/meridian/tag';
import Column from '@amzn/meridian/column';
import Checkbox from '@amzn/meridian/checkbox';
import { PublisherDisplay } from 'src/components/common/PublisherDisplay';

interface CampaignTableRowProps extends CampaignDesiredStateDto {
  onClone?: (campaign: CampaignDesiredStateDto) => void;
  latestWorkflow?: WorkflowDto;
  isSelected?: boolean;
  onToggleSelect?: (campaignId: string) => void;
  skipWorkflowFetch?: boolean;
}

export const CampaignTableRow: React.FC<CampaignTableRowProps> = ({ onClone, latestWorkflow: latestWorkflowProp, isSelected, onToggleSelect, skipWorkflowFetch, ...campaign }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const canManageCampaigns = useCanPerform('MANAGE_CAMPAIGNS');
  const [isWaitingForData, setIsWaitingForData] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef();

  const [updateCampaign] = useCampaignsControllerUpdateMutation();

  const { data: workflowResponse } = useWorkflowsControllerFindManyQuery(
    { programId: campaign.programId, page: 1, pageSize: 100 },
    { skip: !!latestWorkflowProp || !!skipWorkflowFetch || !campaign.programId },
  );

  const latestWorkflow = latestWorkflowProp ?? workflowResponse?.data?.find((w: WorkflowDto) => w.campaignId === campaign.campaignId);

  const { data: publishedState } = useCampaignsControllerGetPublishedQuery(
    { id: campaign.campaignId },
    { skip: false }
  );
  const csvUrl = publishedState?.productSets?.[0]?.catalogCsvUrl;

  const handleDownloadCsv = () => {
    if (!csvUrl) return;
    setIsActionsMenuOpen(false);
    downloadFromS3(csvUrl);
  };

  // Format date helper - matches program list formatting
  const formatDate = (dateString: string | object | undefined) => {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    const dateTime = DateTime.fromISO(dateString).toUTC();
    const formatted = dateTime.toLocaleString(DateTime.DATETIME_FULL);
    const relative = dateTime.toRelativeCalendar();

    return { formatted, relative };
  };

  const showPauseToggle = useMemo(() => (
    campaign.status === CampaignStatuses.PAUSED || campaign.status === CampaignStatuses.ACTIVE
  ), [campaign.status]);

  const pauseToggleText = useMemo(() => (
    campaign.status === CampaignStatuses.PAUSED ? t('form_status_resume') : t('form_status_pause')
  ), [campaign.status, t]);

  const handleStatusUpdateResponse = useCallback((
    response: { data: CampaignDesiredStateDto } | { error: unknown }
  ) => {
    if ('data' in response) {
      createToast({
        type: AlertType.SUCCESS,
        message: `${t('audit_campaign_updated_message')}: ${campaign.campaignId}`,
      });
    } else {
      const {
        data: { error: errorMsg, message },
      } = response.error as RTKWrappedError;

      let extractedMessage: string | undefined;

      if (message && typeof message === 'object') {
        const errors = getErrorsByField(message);
        extractedMessage = errors.status;
      }

      createToast({
        type: AlertType.ERROR,
        message: `${t('form_campaign_error_status')} ${campaign.campaignId}: ${extractedMessage ?? errorMsg}`,
      });

      setIsWaitingForData(false);
    }
  }, [createToast, t, campaign.campaignId]);

  const toggleCampaignPause = useCallback(async (id: string) => {
    setIsWaitingForData(true);

    let newStatus;
    switch (campaign.status) {
      case CampaignStatuses.ACTIVE:
        newStatus = CampaignStatuses.PAUSED;
        break;
      case CampaignStatuses.PAUSED:
        newStatus = CampaignStatuses.ACTIVE;
        break;
      default:
        createToast({
          type: AlertType.ERROR,
          message: `${t('form_campaign_error_status')} ${campaign.campaignId}`,
        });
        setIsWaitingForData(false);
        return;
    }

    const updateCampaignDto: UpdateCampaignDto = {
      ...campaign,
      status: newStatus,
      priority: 'IMMEDIATE',
    };

    const response = await updateCampaign({
      id,
      updateCampaignDto,
    });

    handleStatusUpdateResponse(response);
  }, [campaign, createToast, handleStatusUpdateResponse, t, updateCampaign]);

  const archiveCampaign = useCallback(async () => {
    setIsWaitingForData(true);
    const updateCampaignDto: UpdateCampaignDto = { ...campaign, status: CampaignStatuses.ARCHIVED, priority: 'IMMEDIATE' };
    const response = await updateCampaign({
      id: campaign.campaignId,
      updateCampaignDto,
    });
    handleStatusUpdateResponse(response);
  }, [campaign, handleStatusUpdateResponse, updateCampaign]);

  const unarchiveCampaign = useCallback(async () => {
    setIsWaitingForData(true);
    const updateCampaignDto: UpdateCampaignDto = { ...campaign, status: CampaignStatuses.ACTIVE, priority: 'IMMEDIATE' };
    const response = await updateCampaign({
      id: campaign.campaignId,
      updateCampaignDto,
    });
    handleStatusUpdateResponse(response);
  }, [campaign, handleStatusUpdateResponse, updateCampaign]);

  useEffect(() => {
    setIsWaitingForData(false);
  }, [campaign.status]);

  return (
    <DataTableRow>
      {onToggleSelect && (
        <DataTableCell>
          <Checkbox
            checked={isSelected}
            data-testid={`campaign-select-checkbox-${campaign.campaignId}`}
            onChange={() => { onToggleSelect(campaign.campaignId); }}
          />
        </DataTableCell>
      )}
      <DataTableCell>
        <Column spacing="100">
          <Link
            data-testid={`campaign-${campaign.campaignId}-name-link`}
            style={{ color: '#0073bb', textDecoration: 'none' }}
            to={`/campaigns/${campaign.campaignId}`}
          >
            {campaign.name ?? campaign.campaignId}
          </Link>
          {campaign.name && <Text color="secondary" type="b200">{campaign.campaignId}</Text>}
        </Column>
      </DataTableCell>
      <DataTableCell>
        <PublisherDisplay publisher={campaign.publisher} />
      </DataTableCell>
      <DataTableCell>
        <Tag type={campaign.status === CampaignStatuses.ACTIVE ? 'success' : campaign.status === CampaignStatuses.ARCHIVED ? 'neutral' : 'warning'}>{campaign.status}</Tag>
      </DataTableCell>
      <DataTableCell data-testid={`campaign-${campaign.campaignId}-sync-status`} textAlign="center">
        {latestWorkflow && (
          <Tooltip position="top" title={`workflowId=${latestWorkflow.workflowId} startAt=${latestWorkflow.startTime}`}>
            {{
              [WorkflowStatuses.QUEUED]: <Icon color="warning" data-testid="sync-status-queued" tokens={clockTokens} />,
              [WorkflowStatuses.IN_PROGRESS]: <MeridianLoader data-testid="sync-status-loader" size="medium" />,
              [WorkflowStatuses.COMPLETED]: <Icon color="success" data-testid="sync-status-success" tokens={checkCircleFilledTokens} />,
            }[latestWorkflow.status] ?? <Icon color="error" data-testid="sync-status-error" tokens={alertFilledTokens} />}
          </Tooltip>
        )}
      </DataTableCell>
      <DataTableCell>
        {(() => {
          const dateInfo = formatDate(campaign.createdAt);
          if (dateInfo === 'N/A') return 'N/A';
          return (
            <>
              <Text>{dateInfo.formatted}</Text>
              {dateInfo.relative && (
                <Text color="secondary" type="b200">
                  {dateInfo.relative}
                </Text>
              )}
            </>
          );
        })()}
      </DataTableCell>
      <DataTableCell>
          <Button
            aria-expanded={isActionsMenuOpen}
            aria-haspopup="menu"
            aria-label={t('table_header_actions')}
            data-testid={`campaign-${campaign.campaignId}-actions-menu-button`}
            propagateClickEvent={false}
            ref={actionsMenuRef}
            type="icon"
            onClick={() => { setIsActionsMenuOpen(true); }}
          >
            <Icon tokens={menuMeatballTokens} />
          </Button>
          <Menu
            anchorNode={actionsMenuRef.current}
            minWidth={180}
            open={isActionsMenuOpen}
            position="bottom"
            onClose={() => { setIsActionsMenuOpen(false); }}
          >
            {onClone &&
              <MenuItem
                aria-disabled={isWaitingForData || !canManageCampaigns}
                data-testid={`campaign-${campaign.campaignId}-clone-button`}
                iconTokens={copyTokens}
                onClick={() => {
                  if (isWaitingForData) return;
                  if (!canManageCampaigns) return;
                  setIsActionsMenuOpen(false);
                  onClone(campaign as CampaignDesiredStateDto);
                }}
              >
                {t('clone')}
              </MenuItem>
            }
            {showPauseToggle &&
              <MenuItem
                aria-disabled={isWaitingForData || !canManageCampaigns}
                data-testid={`campaign-${campaign.campaignId}-toggle-pause-button`}
                iconTokens={campaign.status === CampaignStatuses.PAUSED ? playTokens : pauseTokens}
                onClick={() => {
                  if (isWaitingForData) return;
                  if (!canManageCampaigns) return;
                  setIsActionsMenuOpen(false);
                  toggleCampaignPause(campaign.campaignId);
                }}
              >
                {pauseToggleText}
              </MenuItem>
            }
            {campaign.status !== CampaignStatuses.ARCHIVED && (
              <MenuItem
                aria-disabled={isWaitingForData || !canManageCampaigns}
                data-testid={`campaign-${campaign.campaignId}-archive-button`}
                iconTokens={archiveTokens}
                onClick={() => {
                  if (isWaitingForData) return;
                  if (!canManageCampaigns) return;
                  setIsActionsMenuOpen(false);
                  archiveCampaign();
                }}
              >
                {t('archive')}
              </MenuItem>
            )}
            {campaign.status === CampaignStatuses.ARCHIVED && (
              <MenuItem
                aria-disabled={isWaitingForData || !canManageCampaigns}
                data-testid={`campaign-${campaign.campaignId}-unarchive-button`}
                iconTokens={playTokens}
                onClick={() => {
                  if (isWaitingForData) return;
                  if (!canManageCampaigns) return;
                  setIsActionsMenuOpen(false);
                  unarchiveCampaign();
                }}
              >
                {t('unarchive')}
              </MenuItem>
            )}
            <MenuItem
              aria-disabled={!csvUrl}
              data-testid={`csv-download-${campaign.campaignId}`}
              iconTokens={downloadSmallTokens}
              onClick={handleDownloadCsv}
            >
              {t('download_csv')}
            </MenuItem>
          </Menu>
      </DataTableCell>
    </DataTableRow>
  );
};
